import React, { useState } from 'react';

export interface ActionButtonProps {
  onClick: () => void;
  /** Disables the underlying `<button>` and suppresses hover styling. */
  disabled?: boolean;
  /**
   * Controls the `disabledStyle` spread independently of the `disabled`
   * attribute. Submit-reveal buttons disable the element during the
   * submitting choreography without showing the disabled treatment.
   * Defaults to `disabled`.
   */
  disabledStyleActive?: boolean;
  /** Block-specific base style (e.g. `styles.likertSubmitButton`). */
  baseStyle: React.CSSProperties;
  /** Applied while hovered and not disabled (e.g. `styles.actionButtonHover`). */
  hoverStyle: React.CSSProperties;
  /** Applied while `disabledStyleActive` (e.g. `styles.actionButtonDisabled`). */
  disabledStyle?: React.CSSProperties;
  /** Layered last, on top of base/disabled/hover. */
  styleOverride?: React.CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
  children: React.ReactNode;
}

/**
 * Primary action button (Submit / Try Again / Check my answers) that owns
 * its hover state and the base → disabled → hover → override style layering
 * shared by the interaction renderers.
 */
export function ActionButton({
  onClick,
  disabled = false,
  disabledStyleActive,
  baseStyle,
  hoverStyle,
  disabledStyle,
  styleOverride,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  children,
}: ActionButtonProps): React.ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const showDisabledStyle = disabledStyleActive ?? disabled;

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...baseStyle,
        ...(showDisabledStyle && disabledStyle ? disabledStyle : {}),
        ...(isHovered && !disabled ? hoverStyle : {}),
        ...styleOverride,
      }}
    >
      {children}
    </button>
  );
}

ActionButton.displayName = 'ActionButton';
